import { Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material';
import * as moment from 'moment';
import { LoaderService } from 'src/app/loader.service';
import { AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import Utils from 'src/app/utils/utils';
import { SubscriptionService } from '../../subscription.service';
import { subscriptionCreateSuccessAlert, subscriptionOperationFailureAlert } from '../../subscription.create-alert';

export interface DaprResetContext {
  city: { id: string, name: string };
  mode: { id: string, name: string };
  modeServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
  exclusiveOfTax?: boolean;
}

@Component({
  selector: 'app-create-dapr-reset',
  templateUrl: './dapr-reset.create.component.html',
  styleUrls: ['./dapr-reset.create.component.css']
})
export class CreateDaprResetComponent implements OnInit, OnChanges {
  @Input() context: DaprResetContext = null;

  public formGroup = new FormGroup({
    ruleName: new FormControl(null),
    city: new FormControl(null),
    services: new FormControl([]),
    priority: new FormControl(null),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    ruleType: new FormControl({ value: 'qualityReset', disabled: true }),
    minValue: new FormControl(null),
    ruleAmount: new FormControl(null),
    strikedOutAmount: new FormControl(null),
    periodInDays: new FormControl(null)
  });

  public services: { id: string, name: string, serviceDetailId: string, mode?: string, active?: boolean }[] = [];
  public today: Date = moment().startOf('hour').toDate();
  public tomorrowEod: Date = moment().endOf('day').minutes(30).add(1, 'days').toDate();
  public maxStartDate: Date;
  public minEndDate: Date = this.tomorrowEod;
  public Utils = Utils;
  @ViewChild('OverlappingPriorityRuleAlert') OverlappingPriorityRuleAlert: TemplateRef<any>;
  public overlappingRules: any = [];
  private lastServiceKey = '';

  get taxInfoText() {
    return this.context && this.context.exclusiveOfTax
      ? 'This amount is exclusive of tax'
      : 'This amount is inclusive of tax';
  }

  constructor(
    private subsService: SubscriptionService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    public toasterService: ToasterService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.applyContext();
  }

  ngOnChanges() {
    this.applyContext();
  }

  formValue(name: string) {
    return this.formGroup.get(name).value;
  }

  blockInvalidNumberKeys(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  rejectNonPositiveAmount(event: Event, controlName: string) {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value == null) {
      return;
    }
    if (Number(input.value) <= 0) {
      this.formGroup.get(controlName).setValue(null);
      input.value = '';
    }
  }

  changeInDate() {
    const startDate = this.formValue('startDate');
    if (startDate) {
      this.minEndDate = startDate;
    }
    const endDate = this.formValue('endDate');
    if (endDate) {
      this.maxStartDate = endDate;
    }
  }

  errorMessage(message: string) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message
    }));
  }

  validateField(fieldName: string, message: string) {
    const value = this.formValue(fieldName);
    if (!value) {
      this.errorMessage(message);
      throw new Error(message);
    }
    return value;
  }

  async createDaprResetRule() {
    this.loader.openLoading();
    try {
      const ruleName = this.validateField('ruleName', 'Rule name is required');
      const city = this.validateField('city', 'City is required');
      const selectedIds = this.formValue('services') || [];
      if (!selectedIds.length) {
        this.errorMessage('Services is required');
        throw new Error('Services is required');
      }
      const byId = {};
      this.services.forEach(service => {
        byId[service.id] = service;
      });
      const services = selectedIds.map(id => byId[id]).filter(Boolean);
      if (!services.length) {
        this.errorMessage('Services is required');
        throw new Error('Services is required');
      }
      const startTime = moment(this.validateField('startDate', 'Purchase Start date is required').getTime()).unix();
      const endTime = moment(this.validateField('endDate', 'Purchase End date is required').getTime()).unix();
      const priority = this.validateField('priority', 'Priority is required');
      const minValue = this.validateField('minValue', 'Min value is required');
      const ruleAmount = this.validateField('ruleAmount', 'Purchase price is required');
      const periodInDays = this.validateField('periodInDays', 'Period in days is required');
      const serviceDetailIds = services.map(s => s.serviceDetailId);
      const exclusiveOfTax = !!(this.context && this.context.exclusiveOfTax);

      if (this.formValue('strikedOutAmount') && this.formValue('strikedOutAmount') <= ruleAmount) {
        this.errorMessage('Original price should be greater than purchase price');
        throw new Error('Original price should be greater than purchase price');
      }

      const existing: any = await this.subsService.getSamePriorityRules(serviceDetailIds, startTime, endTime, [priority]).toPromise();
      if (existing && existing.length > 0) {
        this.overlappingRules = existing;
        this.dialog.open(this.OverlappingPriorityRuleAlert);
        return;
      }

      const payload = {
        type: 'CASHBACK',
        rules: [{
          ruleName,
          startTime,
          endTime,
          priority: Number(priority),
          city: city.id,
          services: services.map(s => s.id),
          serviceDetails: serviceDetailIds,
          mode: this.context.mode.id,
          isAutoAssignedToHH: false,
          isAutoAssignedToMC: false,
          isDefaultSubscription: false,
          exclusiveOfTax,
          userSelectors: [],
          userSelectorName: null,
          excludeIncentiveTypes: [],
          ruleType: 'qualityReset',
          suspendServicesPostExpiry: true,
          rule: [{
            variable: 'commission',
            operator: '=',
            value: 0,
            type: 'flat'
          }],
          validity: [{ type: 'dapr', minValue: Number(minValue) }],
          ruleAmount: Number(ruleAmount),
          strikedOutAmount: this.formValue('strikedOutAmount'),
          periodInDays: Number(periodInDays),
          isRecommended: false
        }]
      };

      const resp: any = await this.subsService.createSubscriptionRules(payload).toPromise();
      this.alertService.open(subscriptionCreateSuccessAlert(resp, { configRequestKind: 'subscription' }));
    } catch (error) {
      this.alertService.open(subscriptionOperationFailureAlert(error));
    } finally {
      this.loader.closeLoading();
    }
  }

  private applyContext() {
    if (!this.context) {
      return;
    }
    this.formGroup.get('city').setValue(this.context.city, { emitEvent: false });
    const modeId = this.context.mode && this.context.mode.id;
    const nextServices = (this.context.modeServices || []).map(service => Object.assign({}, service, {
      mode: modeId,
      label: service.name + (service.active === false ? ' (inactive)' : '')
    }));
    const key = (this.context.mode && this.context.mode.id) + ':' + nextServices.map(service => service.id).join(',');
    this.services = nextServices;
    if (key !== this.lastServiceKey) {
      this.lastServiceKey = key;
      this.formGroup.get('services').setValue([], { emitEvent: false });
    }
  }
}
